/**
 * Sync Engine for Offline-First Cloud Sync
 * Handles bidirectional sync between IndexedDB and Cloud (AWS RDS)
 * Features:
 * - Auto-sync when online
 * - Conflict resolution
 * - Retry logic
 * - Queue management
 */

import indexedDBService from './indexedDB';
import api from './api';

class SyncEngine {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
    this.isOnline = navigator.onLine;
    this.listeners = [];

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyListeners({ type: 'online' });
      this.syncNow();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners({ type: 'offline' });
    });
  }

  /**
   * Add sync listener
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove sync listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event) {
    this.listeners.forEach(listener => listener(event));
  }

  /**
   * Initialize sync engine
   */
  async init() {
    await indexedDBService.init();

    // Check if cloud sync is enabled
    const cloudSyncEnabled = await indexedDBService.isCloudSyncEnabled();

    if (cloudSyncEnabled && this.isOnline) {
      // Start auto-sync
      this.startAutoSync();

      // Initial sync
      await this.syncNow();
    }
  }

  /**
   * Start automatic sync (every 5 minutes)
   */
  startAutoSync() {
    if (this.syncInterval) return;

    this.syncInterval = setInterval(async () => {
      if (this.isOnline) {
        await this.syncNow();
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Manual sync trigger
   */
  async syncNow() {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return;
    }

    if (!this.isOnline) {
      console.log('Cannot sync: offline');
      return;
    }

    const cloudSyncEnabled = await indexedDBService.isCloudSyncEnabled();
    if (!cloudSyncEnabled) {
      console.log('Cloud sync is disabled');
      return;
    }

    this.isSyncing = true;
    this.notifyListeners({ type: 'sync_started' });

    try {
      // 1. Pull changes from cloud
      await this.pullFromCloud();

      // 2. Push local changes to cloud
      await this.pushToCloud();

      // Update last sync time
      const userId = indexedDBService.getCurrentUserId();
      if (userId) {
        const settings = await indexedDBService.get('appSettings', userId) || {};
        settings.lastSyncAt = new Date().toISOString();
        await indexedDBService.update('appSettings', { ...settings, userId });
      }

      this.notifyListeners({ type: 'sync_completed' });
      console.log('Sync completed successfully');
    } catch (error) {
      console.error('Sync failed:', error);
      this.notifyListeners({ type: 'sync_failed', error });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Pull data from cloud to local IndexedDB
   */
  async pullFromCloud() {
    try {
      // Get last sync timestamp
      const userId = indexedDBService.getCurrentUserId();
      const settings = await indexedDBService.get('appSettings', userId) || {};
      const lastSyncAt = settings.lastSyncAt;

      // Fetch updated data from cloud
      const promises = [
        this.pullEntity('ledgers', lastSyncAt),
        this.pullEntity('transactions', lastSyncAt),
        this.pullEntity('categories', lastSyncAt),
        this.pullEntity('reminders', lastSyncAt),
        this.pullEntity('recurringExpenses', lastSyncAt),
        this.pullEntity('budgets', lastSyncAt),
      ];

      await Promise.all(promises);
      console.log('Pull from cloud completed');
    } catch (error) {
      console.error('Pull from cloud failed:', error);
      throw error;
    }
  }

  /**
   * Pull specific entity from cloud
   */
  async pullEntity(entityType, lastSyncAt) {
    try {
      let endpoint = '';
      let storeName = '';

      switch (entityType) {
        case 'ledgers':
          endpoint = '/ledgers';
          storeName = 'ledgers';
          break;
        case 'transactions':
          endpoint = '/transactions';
          storeName = 'transactions';
          break;
        case 'categories':
          endpoint = '/categories';
          storeName = 'categories';
          break;
        case 'reminders':
          endpoint = '/reminders';
          storeName = 'reminders';
          break;
        case 'recurringExpenses':
          endpoint = '/recurring-expenses';
          storeName = 'recurringExpenses';
          break;
        case 'budgets':
          endpoint = '/budgets';
          storeName = 'budgets';
          break;
        default:
          return;
      }

      // Fetch from API
      const response = await api.get(endpoint, {
        params: lastSyncAt ? { updated_since: lastSyncAt } : {},
      });

      const items = response.data?.data?.[entityType] || response.data?.data || [];

      // Upsert to IndexedDB
      for (const item of items) {
        const localItem = await indexedDBService.get(storeName, item.id);

        if (!localItem) {
          // New item from cloud - add to local
          await indexedDBService.add(storeName, {
            ...item,
            cloudId: item.id,
            syncStatus: 'synced',
          });
        } else {
          // Item exists - check for conflicts
          const cloudUpdatedAt = new Date(item.updated_at || item.created_at);
          const localUpdatedAt = new Date(localItem.localUpdatedAt || localItem.created_at);

          if (cloudUpdatedAt > localUpdatedAt) {
            // Cloud is newer - update local
            await indexedDBService.update(storeName, {
              ...localItem,
              ...item,
              cloudId: item.id,
              syncStatus: 'synced',
            });
          }
          // Else: Local is newer, will be pushed in pushToCloud
        }
      }
    } catch (error) {
      console.error(`Failed to pull ${entityType}:`, error);
    }
  }

  /**
   * Push local changes to cloud
   */
  async pushToCloud() {
    try {
      const pendingItems = await indexedDBService.getPendingSyncItems();

      for (const item of pendingItems) {
        try {
          await this.pushSyncItem(item);
          await indexedDBService.markSyncCompleted(item.id);
        } catch (error) {
          console.error(`Failed to sync item ${item.id}:`, error);

          // Increment retry count
          item.retryCount = (item.retryCount || 0) + 1;

          if (item.retryCount >= 3) {
            item.status = 'failed';
            item.errorMessage = error.message;
          }

          await indexedDBService.update('syncQueue', item);
        }
      }

      console.log('Push to cloud completed');
    } catch (error) {
      console.error('Push to cloud failed:', error);
      throw error;
    }
  }

  /**
   * Push individual sync item
   */
  async pushSyncItem(item) {
    const { entityType, entityId, operation, data } = item;

    let endpoint = '';

    switch (entityType) {
      case 'ledgers':
        endpoint = '/ledgers';
        break;
      case 'transactions':
        endpoint = '/transactions';
        break;
      case 'categories':
        endpoint = '/categories';
        break;
      case 'reminders':
        endpoint = '/reminders';
        break;
      case 'recurringExpenses':
        endpoint = '/recurring-expenses';
        break;
      case 'budgets':
        endpoint = '/budgets';
        break;
      default:
        throw new Error(`Unknown entity type: ${entityType}`);
    }

    switch (operation) {
      case 'create':
        await api.post(endpoint, data);
        break;
      case 'update':
        await api.put(`${endpoint}/${entityId}`, data);
        break;
      case 'delete':
        await api.delete(`${endpoint}/${entityId}`);
        break;
      default:
        throw new Error(`Unknown operation: ${operation}`);
    }
  }

  /**
   * Enable cloud sync
   */
  async enableCloudSync() {
    const userId = indexedDBService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not logged in');
    }

    const settings = await indexedDBService.get('appSettings', userId) || {};
    settings.cloudSyncEnabled = true;
    settings.userId = userId;

    await indexedDBService.update('appSettings', settings);

    // Start auto-sync
    this.startAutoSync();

    // Initial full sync
    await this.syncNow();

    return true;
  }

  /**
   * Disable cloud sync
   */
  async disableCloudSync() {
    const userId = indexedDBService.getCurrentUserId();
    if (!userId) return;

    const settings = await indexedDBService.get('appSettings', userId) || {};
    settings.cloudSyncEnabled = false;
    await indexedDBService.update('appSettings', settings);

    // Stop auto-sync
    this.stopAutoSync();

    return true;
  }

  /**
   * Get sync status
   */
  async getSyncStatus() {
    const userId = indexedDBService.getCurrentUserId();
    if (!userId) return null;

    const settings = await indexedDBService.get('appSettings', userId) || {};
    const pendingItems = await indexedDBService.getPendingSyncItems();

    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      cloudSyncEnabled: settings.cloudSyncEnabled || false,
      lastSyncAt: settings.lastSyncAt || null,
      pendingCount: pendingItems.length,
    };
  }
}

// Create singleton instance
const syncEngine = new SyncEngine();

export default syncEngine;
